import React, { useEffect, useRef, useState } from "react";
import OrganizationUsersTab from "./OrganizationUsersTab";
import {
  OrganizationUserDto,
  UserInvitationCreate,
} from "../common/ObjectTypes";
import InviteUsersModal from "../common/InviteUsersModal";
import {
  createOrganizationUser,
  getOrganizationUsers,
  getUserRoles,
  reinviteOrganizationUser,
  removeUserFromOrganization,
} from "../../api/organizationsApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { OrgUserSearchQueryType } from "../common/QueryTypes";
import { pageSize } from "../../api/apiUtils";

type ManageOrganizationUsersProps = {
  orgId: number;
  orgName: string;
  perms: any[];
};

const ManageOrganizationUsers: React.FC<ManageOrganizationUsersProps> = ({
  orgId,
  orgName,
  perms,
}) => {
  const { t } = useTranslation();
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isUserActionsModalOpen, setUserActionsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserDto>();
  const [activeUsers, setActiveUsers] = useState<OrganizationUserDto[]>([]);
  const [totalActiveUsersCount, setTotalActiveUsersCount] = useState(0);
  const [pendingUsers, setPendingUsers] = useState<OrganizationUserDto[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [inviteRoles, setInviteRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * True while GET /organizations/{id}/roles is in flight. Separate from
   * `loading`, which is the user table's spinner and clears as soon as the
   * first user list lands. The invite modal's Role select reads this so it
   * shows its loading state instead of "No options" while the roles are still
   * on their way (AKL-848, same fix as the project tab).
   */
  const [rolesLoading, setRolesLoading] = useState(true);
  /**
   * Generation counter for `loadRoles`. The effect that calls `fetchData`
   * re-runs on an organization switch and on paging/filter changes, and the
   * tab can call it too, so two roles requests can be in flight at once. Only
   * the newest one may write `roles`/`inviteRoles` or lower `rolesLoading`;
   * otherwise a slow older request could clear the flag while the newer one
   * is still fetching, or land the previous organization's roles on top of
   * the current one's.
   */
  const rolesRequest = useRef(0);
  const [inviteData, setInviteData] = useState<UserInvitationCreate>({
    email: "",
    role_id: 0,
  });
  const handleInviteUsers = () => {
    setInviteModalOpen(true);
  };
  const [sendingInvite, setSendingInvite] = useState(false);
  const [userSearchQuery, setUserSearchQuery] =
    useState<OrgUserSearchQueryType>({
      org_id: orgId,
      is_verified: true,
      role_id: undefined,
      take: pageSize,
      skip: localStorage.getItem("orgUsersTableSkip")
        ? parseInt(localStorage.getItem("orgUsersTableSkip") as string)
        : 0,
    });

  const loadActiveUsers = async (query: any) => {
    try {
      setLoading(true);
      const temp = await getOrganizationUsers(query);
      setActiveUsers(temp.users);
      setTotalActiveUsersCount(temp.count);
    } catch (err) {
      console.error("Error loading active users:", err);
    } finally {
      setLoading(false);
    }
  };
  const loadPendingUsers = async (query: any) => {
    try {
      setLoading(true);
      const temp = await getOrganizationUsers(query);
      setPendingUsers(temp.users);
    } catch (err) {
      console.error("Error loading pending users:", err);
    } finally {
      setLoading(false);
    }
  };
  /**
   * Loads the active users, the pending invitations and the grantable roles.
   *
   * The three requests are independent, so they run in parallel rather than
   * one after another with the roles last — on a slow API the sequential order
   * left the Invite User dropdown reading "No options" until the third request
   * came back (AKL-848). Each loader catches its own errors, so one failing
   * does not stop the others.
   */
  const fetchData = async () => {
    const loadRoles = async () => {
      const request = ++rolesRequest.current;
      const isLatest = () => request === rolesRequest.current;
      setRolesLoading(true);
      try {
        const temp = await getUserRoles(orgId);
        // A newer request owns the state now; let it finish the job.
        if (!isLatest()) return;
        setRoles(temp);
        // Deliberately unfiltered, for the same reason as the project tab —
        // see AKL-848. GET /organizations/{id}/roles is already built from the
        // caller's own INVITE_USER_AS_OWNER / _EDITOR_CONTRIBUTOR / _VIEWER.
        // This page gates its render on permissions having loaded, so it never
        // showed the empty dropdown, but switching straight from one
        // organization's Users tab to another's could filter the new org's
        // roles through the previous org's permissions.
        setInviteRoles(temp);
      } catch (err) {
        console.error("Error loading roles:", err);
      } finally {
        if (isLatest()) setRolesLoading(false);
      }
    };

    try {
      await Promise.all([
        loadActiveUsers(userSearchQuery),
        loadPendingUsers({ ...userSearchQuery, is_verified: false }),
        loadRoles(),
      ]);
    } catch (err) {
      console.error("Error loading roles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [orgId, userSearchQuery.role_id, userSearchQuery.skip]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setInviteData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeSearch = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setUserSearchQuery((prevState) => ({
      ...prevState,
      [name]: value,
      skip: 0,
    }));
  };

  const handleSelectRole = (selectedOption: any) => {
    setUserSearchQuery((prevState) => ({
      ...prevState,
      role_id: selectedOption,
      skip: 0,
    }));
  };

  const handleSearch = async () => {
    let query = {
      ...userSearchQuery,
      skip: 0,
    };
    await loadActiveUsers(query);
    await loadPendingUsers({ ...query, is_verified: false });
  };

  const handleRoleSelectEnter = async (e: React.KeyboardEvent) => {
    if (userSearchQuery.role_id != null) {
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({ ...userSearchQuery, is_verified: false });
    }
  };

  const handleResendInvitation = async (user_id: string) => {
    try {
      setSendingInvite(true);
      await reinviteOrganizationUser(orgId, user_id, orgId);
      toast.success(t("Invitation Resent"));
    } catch (err) {
      toast.error(t("Error Resending Invitation"));
    } finally {
      setSendingInvite(false);
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({ ...userSearchQuery, is_verified: false });
    }
  };

  const handleRemoveInvitation = async (user_id: string) => {
    try {
      setLoading(true);
      await removeUserFromOrganization(orgId, user_id);
      toast.success(t("User Removed"));
    } catch (err) {
      toast.error(t("Error Removing Invitation"));
    } finally {
      setLoading(false);
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({ ...userSearchQuery, is_verified: false });
    }
  };

  const handleSelectUser = (user_id: number) => {
    // Find the user object based on id
    const user = activeUsers.find((user: any) => user.user_id === user_id);
    if (user !== undefined) {
      setSelectedUser(user);
    }
    // Set the selectedUser as the selected user, or null if not found (this shouldn't really happen if ids are correct)
    setUserActionsModalOpen(true);
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    try {
      setSendingInvite(true);
      await createOrganizationUser(orgId, inviteData);
      setInviteData({
        email: "",
        role_id: 0,
      });
      setSendingInvite(false);
      toast.success(t("Invite sent successfully!"));
      setInviteModalOpen(false);
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({ ...userSearchQuery, is_verified: false });
    } catch (error: any) {
      console.error("Error sending invite:", error);
      setSendingInvite(false);
      toast.error(t("Error sending invite:") + error.data.detail.toString());
    }
  };

  const handleSelectUsersRole = (selectedOption: any) => {
    setInviteData((prevState) => ({
      ...prevState,
      role_id: selectedOption?.value,
    }));
  };

  const handleClose = () => {
    setInviteModalOpen(false);
    setInviteData({
      email: "",
      role_id: 0,
    });
  };

  const handlePageClick = (e: any) => {
    setUserSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: e.selected * pageSize,
    }));
    localStorage.setItem(
      "orgUsersTableSkip",
      (e.selected * pageSize).toString(),
    );
    setLoading(true);
  };

  return (
    <>
      <OrganizationUsersTab
        activeUsers={activeUsers}
        pendingUsers={pendingUsers}
        userSearchQuery={userSearchQuery}
        take={Number(userSearchQuery.take)}
        totalCount={totalActiveUsersCount}
        offset={Number(userSearchQuery.skip)}
        onPageClick={handlePageClick}
        onInviteUser={handleInviteUsers}
        onSelectUser={handleSelectUser}
        isOpen={isUserActionsModalOpen}
        onClose={() => setUserActionsModalOpen(false)}
        selectedUser={selectedUser}
        loading={loading}
        roles={roles}
        orgId={orgId}
        orgName={orgName}
        perms={perms}
        loadUsersAndRoles={fetchData}
        onResendInvitation={handleResendInvitation}
        onRemoveInvitation={handleRemoveInvitation}
        sendingInvite={sendingInvite}
        onChangeSearch={handleChangeSearch}
        onSearch={handleSearch}
        onChangeSelectRole={handleSelectRole}
        handleRoleSelectEnter={handleRoleSelectEnter}
      />

      <InviteUsersModal
        roles={inviteRoles}
        rolesLoading={rolesLoading}
        scope="organization"
        inviteData={inviteData}
        isOpen={isInviteModalOpen}
        onSelectUsersRole={handleSelectUsersRole}
        onClose={handleClose}
        onSendInvite={handleSendInvite}
        onChange={handleChange}
        sendingInvite={sendingInvite}
      />
    </>
  );
};

export default ManageOrganizationUsers;
